/**
 * 连续破折号连接工具。
 *
 * 背景：中文正文里的“——”是两个 U+2014 EM DASH。FangSong/仿宋 等字体中
 * 每个 EM DASH 自带左右侧轴承（side bearing），两个字面并排时中间会留出
 * 一条细小空白，视觉上不像一个连续的破折号。
 *
 * 这里通过给连续的“——”包一层 `.dash-connector`，用负 letter-spacing 抵消
 * 两个字符之间的侧轴承间隙，同时用 margin-right 补偿最后一个字符后的
 * letter-spacing，避免影响后续文字间距。
 */

export const DASH_CONNECTOR_CLASS = 'dash-connector';

const EM_DASH_PAIR = '——';
const EM_DASH_PAIR_PATTERN = /(——)/g;

const HTML_ESCAPE: Readonly<Record<string, string>> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

/**
 * 给纯文本摘要使用：先做 HTML 转义（摘要不是富文本，不能直接注入 HTML），
 * 再把连续的“——”替换成带连接样式的 span。
 */
export function connectEmDashesInText(text: string): string {
  const escaped = text.replace(/[&<>"']/g, (char) => HTML_ESCAPE[char] ?? char);
  return escaped.replace(
    EM_DASH_PAIR_PATTERN,
    `<span class="${DASH_CONNECTOR_CLASS}">${EM_DASH_PAIR}</span>`,
  );
}

/**
 * 给富文本 DOM 使用：在渲染后的 DOM 上遍历文本节点，把“——”包成
 * `.dash-connector` span，不触碰其它 HTML 结构。
 */
export function connectEmDashesInDom(root: HTMLElement): void {
  const textNodes: Text[] = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  while (walker.nextNode()) {
    const node = walker.currentNode;
    if (node instanceof Text && node.textContent?.includes(EM_DASH_PAIR)) {
      textNodes.push(node);
    }
  }

  for (const node of textNodes) {
    const parent = node.parentNode;
    if (!parent) {
      continue;
    }

    const text = node.textContent ?? '';
    const parts = text.split(EM_DASH_PAIR_PATTERN);
    const fragment = document.createDocumentFragment();

    for (const part of parts) {
      if (part === EM_DASH_PAIR) {
        const span = document.createElement('span');
        span.className = DASH_CONNECTOR_CLASS;
        span.textContent = EM_DASH_PAIR;
        fragment.appendChild(span);
      } else if (part) {
        fragment.appendChild(document.createTextNode(part));
      }
    }

    parent.replaceChild(fragment, node);
  }
}
